import type { DayRollup } from '@baby-tracker/contracts';
import {
    calendarDateSchema,
    type CalendarDate,
    enumerateCalendarDates,
    shiftCalendarDate,
} from '@baby-tracker/validation';
import type { ReactElement } from 'react';

import { Button } from '../../ui/Button.js';
import fieldStyles from '../../ui/fields.module.css';
import styles from './tracking.module.css';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export function MonthCalendar({
    month,
    selected,
    days,
    onMonth,
    onSelect,
}: {
    month: string;
    selected: CalendarDate | undefined;
    days: DayRollup[] | undefined;
    onMonth: (month: string) => void;
    onSelect: (date: CalendarDate) => void;
}): ReactElement {
    const start = calendarDateSchema.parse(`${month}-01`);
    const end = shiftCalendarDate(`${shiftCalendarDate(start, 32).slice(0, 7)}-01`, -1);
    const dates = enumerateCalendarDates(start, end);
    const leading = (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7;
    const marked = new Set(
        (days ?? []).filter((day) => dayHasActivity(day)).map((day) => day.date),
    );
    return (
        <div>
            <div className={fieldStyles.row}>
                <Button
                    variant="secondary"
                    onClick={() => {
                        onMonth(shiftCalendarDate(start, -1).slice(0, 7));
                    }}
                >
                    Previous month
                </Button>
                <p className={styles.summary}>{monthLabel(start)}</p>
                <Button
                    variant="secondary"
                    onClick={() => {
                        onMonth(shiftCalendarDate(end, 1).slice(0, 7));
                    }}
                >
                    Next month
                </Button>
            </div>
            <div className={styles.month}>
                {WEEKDAYS.map((day) => (
                    <span key={day} className={styles.weekday}>
                        {day}
                    </span>
                ))}
                {Array.from({ length: leading }, (_, index) => (
                    <span key={`pad-${index}`} />
                ))}
                {dates.map((date) => (
                    <Button
                        key={date}
                        variant={date === selected ? 'primary' : 'ghost'}
                        className={marked.has(date) ? styles.dayMarked : styles.day}
                        aria-pressed={date === selected}
                        onClick={() => {
                            onSelect(date);
                        }}
                    >
                        {Number(date.slice(8))}
                    </Button>
                ))}
            </div>
        </div>
    );
}

function monthLabel(start: CalendarDate): string {
    return new Intl.DateTimeFormat(undefined, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(`${start}T00:00:00Z`));
}

function dayHasActivity(day: DayRollup): boolean {
    if (day.pumpingMl > 0) {
        return true;
    }
    return day.children.some(
        (child) =>
            child.breastSeconds > 0 ||
            child.bottleMl > 0 ||
            child.solidCount > 0 ||
            child.sleepSeconds > 0 ||
            child.nappyCount > 0,
    );
}
